/**
 * <admira-carport-map> — sections/admira-carport-map.liquid.
 *
 * Loads Leaflet when the section nears the viewport, centres the map on
 * Romania, and turns a click (or the visitor's location, or a dragged marker)
 * into Keystone's yearly-output estimate.
 */

const LEAFLET = {
  css: 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css',
  cssIntegrity: 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=',
  js: 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js',
  jsIntegrity: 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=',
};

const ROMANIA = /** @type {[number, number]} */ ([45.9432, 24.9668]);

/** @type {Promise<any> | null} */
let leafletPromise = null;

/** @returns {Promise<any>} the global `L` */
function loadLeaflet() {
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise((resolve, reject) => {
    // @ts-ignore — Leaflet's UMD global
    if (window.L) return resolve(window.L);

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = LEAFLET.css;
    link.integrity = LEAFLET.cssIntegrity;
    link.crossOrigin = '';
    document.head.append(link);

    const script = document.createElement('script');
    script.src = LEAFLET.js;
    script.integrity = LEAFLET.jsIntegrity;
    script.crossOrigin = '';
    // @ts-ignore — Leaflet's UMD global
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.head.append(script);
  });

  return leafletPromise;
}

/**
 * Keystone's model: kWh/kWp ≈ 1600 − 6·|lat|, held to 900–1800, with up to
 * 8% off as the tilt moves away from the latitude (itself held to 10–40°).
 * @param {number} lat
 * @param {number} tilt
 */
function estimateKwhPerKwp(lat, tilt) {
  const base = Math.max(900, Math.min(1800, 1600 - 6 * Math.abs(lat)));
  const optimal = Math.max(10, Math.min(40, Math.abs(lat)));
  const tiltFactor = 1 - Math.min(0.08, Math.abs(tilt - optimal) * 0.004);
  return Math.round(base * tiltFactor);
}

class AdmiraCarportMap extends HTMLElement {
  /** @type {any} */ #map = null;
  /** @type {any} */ #marker = null;
  /** @type {any} */ #L = null;
  /** @type {IntersectionObserver | null} */ #observer = null;

  connectedCallback() {
    this.addEventListener('input', this.#recalculate);
    this.querySelector('[data-locate]')?.addEventListener('click', this.#locate);
    this.querySelector('[data-reset]')?.addEventListener('click', this.#reset);

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#initMap();
      },
      { rootMargin: '400px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#map?.remove();
    this.#map = null;
    this.#marker = null;
  }

  async #initMap() {
    const el = this.querySelector('[data-map]');
    if (!el) return;

    try {
      this.#L = await loadLeaflet();
    } catch {
      return;
    }

    const L = this.#L;
    this.#map = L.map(el, { scrollWheelZoom: true }).setView(ROMANIA, 6);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(this.#map);

    this.#map.on('click', (/** @type {any} */ event) => this.#setMarker(event.latlng.lat, event.latlng.lng));
  }

  /**
   * @param {number} lat
   * @param {number} lng
   */
  #setMarker(lat, lng) {
    const L = this.#L;
    if (!L || !this.#map) return;

    if (this.#marker) {
      this.#marker.setLatLng([lat, lng]);
    } else {
      const icon = L.divIcon({
        className: 'admira-carport-map__marker',
        iconSize: [26, 26],
        iconAnchor: [13, 13],
      });
      this.#marker = L.marker([lat, lng], { draggable: true, icon }).addTo(this.#map);
      this.#marker.on('dragend', this.#recalculate);
    }

    this.#recalculate();
  }

  #recalculate = () => {
    if (!this.#marker) return;
    const { lat, lng } = this.#marker.getLatLng();

    const kwp = Math.max(0, parseFloat(this.#value('kwp')) || 0);
    const shade = Math.max(0, Math.min(80, parseFloat(this.#value('shade')) || 0));
    const tilt = Math.max(0, Math.min(60, parseFloat(this.#value('tilt')) || 15));

    const kwhPerKwp = estimateKwhPerKwp(lat, tilt);
    const annual = Math.round(kwhPerKwp * kwp * (1 - shade / 100));

    this.#output('lat', lat.toFixed(5));
    this.#output('lng', lng.toFixed(5));
    this.#output('kwhPerKwp', kwhPerKwp.toLocaleString('ro-RO'));
    this.#output('annual', annual.toLocaleString('ro-RO'));
    this.#output('monthly', Math.round(annual / 12).toLocaleString('ro-RO'));
  };

  #locate = () => {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-locate]'));
    if (!button) return;

    if (!navigator.geolocation) {
      this.#status(button.dataset.unavailable);
      return;
    }

    button.disabled = true;
    button.textContent = button.dataset.busy ?? '';
    this.#status('');

    const done = () => {
      button.disabled = false;
      button.textContent = button.dataset.label ?? '';
    };

    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        done();
        if (!this.#map) await this.#initMap();
        this.#map?.setView([coords.latitude, coords.longitude], 13);
        this.#setMarker(coords.latitude, coords.longitude);
      },
      () => {
        done();
        this.#status(button.dataset.failed);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  #reset = () => {
    if (this.#marker) {
      this.#map?.removeLayer(this.#marker);
      this.#marker = null;
    }
    for (const key of ['lat', 'lng', 'kwhPerKwp', 'annual', 'monthly']) this.#output(key, '—');
    this.#status('');
  };

  /** @param {string} name */
  #value(name) {
    const input = this.querySelector(`input[name="${name}"]`);
    return input instanceof HTMLInputElement ? input.value : '';
  }

  /**
   * @param {string} key
   * @param {string} text
   */
  #output(key, text) {
    const el = this.querySelector(`[data-out="${key}"]`);
    if (el) el.textContent = text;
  }

  /** @param {string | undefined} text */
  #status(text) {
    const el = this.querySelector('[data-status]');
    if (el) el.textContent = text ?? '';
  }
}

if (!customElements.get('admira-carport-map')) {
  customElements.define('admira-carport-map', AdmiraCarportMap);
}
